import type { CSSProperties } from 'react'
import { useState, useEffect, useRef, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../api/client'
import type { FoodItem, MealType } from '../types'
import { BarcodeScanner } from '../components/BarcodeScanner'
import { FoodCreateModal } from '../components/FoodCreateModal'

type Tab = 'recent' | 'search' | 'favourites'

const MEAL_LABELS: Record<string, string> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
  snack: 'Snack',
}

const MEAL_OPTIONS: MealType[] = ['breakfast', 'lunch', 'dinner', 'snack']

// ─── Food detail bottom sheet ────────────────────────────────────────────────

function FoodSheet({
  food,
  meal: initialMeal,
  date,
  onClose,
  onLogged,
}: {
  food: FoodItem
  meal: MealType
  date: string
  onClose: () => void
  onLogged: () => void
}) {
  const [servings, setServings] = useState('1')
  const [meal, setMeal] = useState<MealType>(initialMeal)
  const queryClient = useQueryClient()

  const { data: favList = [] } = useQuery({
    queryKey: ['foods', 'favourites'],
    queryFn: () => api.favourites.list() as Promise<FoodItem[]>,
  })

  // Sync fav status once list loads
  const isCurrentlyFav = favList.some((f) => f.id === food.id)

  const toggleFav = useMutation({
    mutationFn: () =>
      isCurrentlyFav
        ? api.favourites.remove(food.id)
        : api.favourites.add(food.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['foods', 'favourites'] })
    },
  })

  const addLog = useMutation({
    mutationFn: () =>
      api.logs.food.add({
        log_date: date,
        meal_type: meal,
        food_item_id: food.id,
        servings: parseFloat(servings) || 1,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['summary', 'day', date] })
      queryClient.invalidateQueries({ queryKey: ['foods', 'recent'] })
      onLogged()
    },
  })

  const sv = parseFloat(servings) || 1
  const ratio = (sv * food.serving_size_g) / 100
  const calc = (v: number | null) => (v != null ? Math.round(v * ratio * 10) / 10 : null)

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.6)',
          zIndex: 40,
        }}
      />

      {/* Sheet */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: '#18181b',
          borderRadius: '16px 16px 0 0',
          padding: '1.25rem 1rem',
          zIndex: 50,
          paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom))',
        }}
      >
        {/* Handle */}
        <div
          style={{
            width: 36,
            height: 4,
            background: '#3f3f46',
            borderRadius: 2,
            margin: '0 auto 1rem',
          }}
        />

        {/* Food name + favourite */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 600, lineHeight: 1.3 }}>{food.name}</h2>
            {food.brand && (
              <p style={{ fontSize: '0.8rem', color: '#71717a', marginTop: 2 }}>{food.brand}</p>
            )}
          </div>
          <button
            onClick={() => toggleFav.mutate()}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.3rem',
              cursor: 'pointer',
              padding: '0 0 0 0.5rem',
              flexShrink: 0,
              opacity: toggleFav.isPending ? 0.5 : 1,
            }}
            aria-label={isCurrentlyFav ? 'Remove from favourites' : 'Add to favourites'}
          >
            {isCurrentlyFav ? '★' : '☆'}
          </button>
        </div>

        {/* Per 100g label */}
        <p style={{ fontSize: '0.72rem', color: '#52525b', marginBottom: '0.75rem' }}>
          Per 100g
        </p>

        {/* Macro row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '0.5rem',
            marginBottom: '1rem',
          }}
        >
          {[
            { label: 'Cal', value: calc(food.calories), unit: '', color: '#fafafa' },
            { label: 'Protein', value: calc(food.protein_g), unit: 'g', color: '#22d3ee' },
            { label: 'Carbs', value: calc(food.carbs_g), unit: 'g', color: '#a78bfa' },
            { label: 'Fat', value: calc(food.fat_g), unit: 'g', color: '#fb923c' },
          ].map(({ label, value, unit, color }) => (
            <div
              key={label}
              style={{
                background: '#0f0f11',
                borderRadius: 8,
                padding: '0.5rem',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: '1rem', fontWeight: 700, color }}>
                {value != null ? value : '—'}
                <span style={{ fontSize: '0.6rem', fontWeight: 400 }}>{unit}</span>
              </div>
              <div style={{ fontSize: '0.65rem', color: '#71717a' }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Servings input */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ fontSize: '0.8rem', color: '#a1a1aa', display: 'block', marginBottom: 4 }}>
            Servings (1 serving = {food.serving_size_g}g)
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              onClick={() => setServings((s) => String(Math.max(0.25, (parseFloat(s) || 1) - 0.25)))}
              style={stepBtn}
            >
              −
            </button>
            <input
              type="number"
              value={servings}
              min="0.25"
              step="0.25"
              onChange={(e) => setServings(e.target.value)}
              style={servingInput}
            />
            <button
              onClick={() => setServings((s) => String((parseFloat(s) || 1) + 0.25))}
              style={stepBtn}
            >
              +
            </button>
          </div>
        </div>

        {/* Meal picker + Add button */}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <select
            value={meal}
            onChange={(e) => setMeal(e.target.value as MealType)}
            style={{
              background: '#27272a',
              border: '1px solid #3f3f46',
              borderRadius: 10,
              color: '#fafafa',
              fontSize: '0.85rem',
              padding: '0 0.6rem',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            {MEAL_OPTIONS.map((m) => (
              <option key={m} value={m}>{MEAL_LABELS[m]}</option>
            ))}
          </select>
          <button
            onClick={() => addLog.mutate()}
            disabled={addLog.isPending}
            style={{ ...addFoodBtn, flex: 1 }}
          >
            {addLog.isPending ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>
    </>
  )
}

// ─── Food list item ───────────────────────────────────────────────────────────

function FoodListItem({ food, onSelect }: { food: FoodItem; onSelect: (f: FoodItem) => void }) {
  return (
    <button
      onClick={() => onSelect(food)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        background: 'none',
        border: 'none',
        borderBottom: '1px solid #18181b',
        padding: '0.7rem 1rem',
        cursor: 'pointer',
        textAlign: 'left',
        color: '#fafafa',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: '0.875rem',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {food.name}
        </div>
        {food.brand && (
          <div style={{ fontSize: '0.75rem', color: '#71717a' }}>{food.brand}</div>
        )}
      </div>
      <div style={{ marginLeft: '0.75rem', textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>{food.calories}</div>
        <div style={{ fontSize: '0.65rem', color: '#71717a' }}>kcal/100g</div>
      </div>
    </button>
  )
}

// ─── Main LogPage ─────────────────────────────────────────────────────────────

export default function LogPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()

  const meal = (params.get('meal') ?? 'snack') as MealType
  const date = params.get('date') ?? new Date().toISOString().slice(0, 10)

  const [tab, setTab] = useState<Tab>('recent')
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [selectedFood, setSelectedFood] = useState<FoodItem | null>(null)
  const [showScanner, setShowScanner] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createInitialName, setCreateInitialName] = useState('')
  const [barcodeNotFound, setBarcodeNotFound] = useState<string | null>(null) // the barcode string
  const searchRef = useRef<HTMLInputElement>(null)

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 350)
    return () => clearTimeout(t)
  }, [query])

  // Switch to search tab when user types
  useEffect(() => {
    if (query.length > 0) setTab('search')
  }, [query])

  const { data: recentFoods = [] } = useQuery({
    queryKey: ['foods', 'recent'],
    queryFn: () => api.favourites.recent() as Promise<FoodItem[]>,
    enabled: tab === 'recent',
  })

  const { data: favourites = [] } = useQuery({
    queryKey: ['foods', 'favourites'],
    queryFn: () => api.favourites.list() as Promise<FoodItem[]>,
    enabled: tab === 'favourites',
  })

  const { data: searchResults = [], isFetching: searching } = useQuery({
    queryKey: ['foods', 'search', debouncedQuery],
    queryFn: () => api.foods.search(debouncedQuery) as Promise<FoodItem[]>,
    enabled: debouncedQuery.length >= 2,
  })

  const handleSelect = useCallback((food: FoodItem) => {
    setSelectedFood(food)
  }, [])

  const handleLogged = useCallback(() => {
    setSelectedFood(null)
    navigate(`/day`)
  }, [navigate])

  const handleBarcodeScan = useCallback(async (barcode: string) => {
    setShowScanner(false)
    setBarcodeNotFound(null)
    try {
      const food = await api.foods.byBarcode(barcode)
      setSelectedFood(food)
    } catch {
      setBarcodeNotFound(barcode)
    }
  }, [])

  const handleCreated = useCallback((food: FoodItem) => {
    setShowCreateModal(false)
    setBarcodeNotFound(null)
    setSelectedFood(food)
  }, [])

  const visibleFoods: FoodItem[] =
    tab === 'search'
      ? searchResults
      : tab === 'favourites'
      ? favourites
      : recentFoods

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '0.75rem 1rem',
          borderBottom: '1px solid #27272a',
          background: '#09090b',
        }}
      >
        <button
          onClick={() => navigate('/day')}
          style={{ background: 'none', border: 'none', color: '#fafafa', cursor: 'pointer', fontSize: '1.1rem', padding: 0 }}
          aria-label="Back"
        >
          ←
        </button>
        <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>
          Add to {MEAL_LABELS[meal] ?? meal}
        </span>
      </div>

      {/* Search input */}
      <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #27272a' }}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <span
              style={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#52525b',
                fontSize: '0.9rem',
                pointerEvents: 'none',
              }}
            >
              🔍
            </span>
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search foods…"
              autoComplete="off"
              style={searchInput}
            />
            {query && (
              <button
                onClick={() => { setQuery(''); setTab('recent') }}
                style={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#52525b',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  padding: 0,
                }}
              >
                ✕
              </button>
            )}
          </div>
          {/* Barcode scan button */}
          <button
            onClick={() => setShowScanner(true)}
            title="Scan barcode"
            style={{
              flexShrink: 0,
              width: 40,
              height: 40,
              background: '#27272a',
              border: 'none',
              borderRadius: 10,
              color: '#fafafa',
              fontSize: '1.1rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            〔▮〕
          </button>
        </div>

        {/* Barcode not found — prompt to add manually */}
        {barcodeNotFound && (
          <div
            style={{
              marginTop: '0.5rem',
              background: '#1c1109',
              border: '1px solid #78350f',
              borderRadius: 8,
              color: '#fcd34d',
              fontSize: '0.8rem',
              padding: '0.5rem 0.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.5rem',
            }}
          >
            <span>Not found — add it manually?</span>
            <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
              <button
                onClick={() => {
                  setCreateInitialName('')
                  setShowCreateModal(true)
                }}
                style={{
                  background: '#78350f',
                  border: 'none',
                  borderRadius: 6,
                  color: '#fde68a',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  padding: '0.3rem 0.6rem',
                  cursor: 'pointer',
                }}
              >
                Add manually
              </button>
              <button
                onClick={() => setBarcodeNotFound(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#92400e',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  padding: '0.3rem',
                }}
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tabs + Create food */}
      <div
        style={{
          display: 'flex',
          borderBottom: '1px solid #27272a',
          background: '#09090b',
        }}
      >
        {(['recent', 'favourites', 'search'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              flex: 1,
              background: 'none',
              border: 'none',
              borderBottom: `2px solid ${tab === t ? '#22d3ee' : 'transparent'}`,
              color: tab === t ? '#22d3ee' : '#71717a',
              fontSize: '0.78rem',
              fontWeight: tab === t ? 600 : 400,
              padding: '0.6rem 0',
              cursor: 'pointer',
              textTransform: 'capitalize',
              transition: 'color 0.15s',
            }}
          >
            {t}
          </button>
        ))}
        <button
          onClick={() => { setCreateInitialName(query); setShowCreateModal(true) }}
          style={{
            background: 'none',
            border: 'none',
            borderBottom: '2px solid transparent',
            color: '#a78bfa',
            fontSize: '0.78rem',
            fontWeight: 600,
            padding: '0.6rem 0.75rem',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
          title="Create a new food"
        >
          + Create
        </button>
      </div>

      {/* Food list */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {tab === 'search' && debouncedQuery.length < 2 && (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#52525b', fontSize: '0.85rem' }}>
            Type at least 2 characters to search
          </div>
        )}

        {tab === 'search' && searching && debouncedQuery.length >= 2 && (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#71717a', fontSize: '0.85rem' }}>
            Searching…
          </div>
        )}

        {tab === 'search' && !searching && debouncedQuery.length >= 2 && searchResults.length === 0 && (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#52525b', fontSize: '0.85rem' }}>
            <div style={{ marginBottom: '0.75rem' }}>No results for "{debouncedQuery}"</div>
            <button
              onClick={() => { setCreateInitialName(debouncedQuery); setShowCreateModal(true) }}
              style={{
                background: '#1a0f2e',
                border: '1px solid #7c3aed',
                borderRadius: 8,
                color: '#a78bfa',
                fontSize: '0.8rem',
                fontWeight: 600,
                padding: '0.45rem 1rem',
                cursor: 'pointer',
              }}
            >
              + Create "{debouncedQuery}"
            </button>
          </div>
        )}

        {(tab !== 'search' || (!searching && debouncedQuery.length >= 2)) &&
          visibleFoods.map((food) => (
            <FoodListItem key={food.id} food={food} onSelect={handleSelect} />
          ))}

        {tab === 'recent' && recentFoods.length === 0 && (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#52525b', fontSize: '0.85rem' }}>
            No recent foods yet - search to add your first entry
          </div>
        )}

        {tab === 'favourites' && favourites.length === 0 && (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#52525b', fontSize: '0.85rem' }}>
            No favourites yet
          </div>
        )}
      </div>

      {/* Food detail sheet */}
      {selectedFood && (
        <FoodSheet
          food={selectedFood}
          meal={meal}
          date={date}
          onClose={() => setSelectedFood(null)}
          onLogged={handleLogged}
        />
      )}

      {/* Barcode scanner */}
      {showScanner && (
        <BarcodeScanner
          onScan={handleBarcodeScan}
          onClose={() => setShowScanner(false)}
        />
      )}

      {/* Manual food creation */}
      {showCreateModal && (
        <FoodCreateModal
          meal={meal}
          date={date}
          initialName={createInitialName}
          initialBarcode={barcodeNotFound ?? undefined}
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  )
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const searchInput: CSSProperties = {
  width: '100%',
  background: '#18181b',
  border: '1px solid #27272a',
  borderRadius: 10,
  color: '#fafafa',
  fontSize: '0.9rem',
  padding: '0.55rem 2.2rem',
  outline: 'none',
}

const stepBtn: CSSProperties = {
  width: 36,
  height: 36,
  background: '#27272a',
  border: 'none',
  borderRadius: 8,
  color: '#fafafa',
  fontSize: '1.1rem',
  cursor: 'pointer',
  flexShrink: 0,
}

const servingInput: CSSProperties = {
  flex: 1,
  background: '#27272a',
  border: '1px solid #3f3f46',
  borderRadius: 8,
  color: '#fafafa',
  fontSize: '1rem',
  textAlign: 'center',
  padding: '0.4rem',
  outline: 'none',
}

const addFoodBtn: CSSProperties = {
  width: '100%',
  background: '#22d3ee',
  border: 'none',
  borderRadius: 10,
  color: '#09090b',
  fontSize: '0.95rem',
  fontWeight: 700,
  padding: '0.75rem',
  cursor: 'pointer',
}
